import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { useProjectStore } from '../project/project-store';
import type { LeaseTractSharedAcreageTarget } from '../types/api';

/**
 * Queries/mutations for `public.lease_tract_shared_acreage` (spec
 * docs/superpowers/specs/2026-08-16-land-data-lease-tracts-design.md §3.2)
 * — a self-referential link between two `lease_tracts` rows in the same
 * project. Modeled on queries/xrefs.ts, but there's only ONE related type
 * (another tract), so the shape is simpler: no per-type grouping.
 */

export const leaseTractSharedAcreageKeys = {
  all: ['lease-tract-shared-acreage'] as const,
  detail: (projectId: string | null, tractId: string) =>
    [...leaseTractSharedAcreageKeys.all, projectId, tractId] as const,
};

export function useSharedAcreage(tractId: string | null) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: tractId
      ? leaseTractSharedAcreageKeys.detail(projectId, tractId)
      : [...leaseTractSharedAcreageKeys.all, 'none'],
    enabled: tractId != null && !!projectId,
    queryFn: async () => {
      const res = await api.get<{ tracts: LeaseTractSharedAcreageTarget[] }>(
        `/lease-tracts/${tractId}/shared-acreage`,
      );
      return res.tracts;
    },
  });
}

export interface AddSharedAcreageInput {
  target_tract_id: string;
  remark?: string | null | undefined;
}

interface SharedAcreageLinkResponse {
  shared_acreage: { id: string; project_id: string; deleted_at: string | null; [k: string]: unknown };
}

/**
 * Links `tractId` to `input.target_tract_id`. On success, invalidates BOTH
 * this tract's shared-acreage query AND the target tract's, so a link added
 * from one drawer shows up immediately if the other tract's drawer is open
 * too (bidirectional by construction — same pattern as useAddXref).
 */
export function useAddSharedAcreage(tractId: string | null) {
  const qc = useQueryClient();
  const projectId = useProjectStore((s) => s.projectId);
  return useMutation({
    mutationFn: (input: AddSharedAcreageInput) => {
      if (tractId == null) {
        return Promise.reject(
          new Error('Cannot link shared acreage before the tract is saved.'),
        );
      }
      return api.post<SharedAcreageLinkResponse>(`/lease-tracts/${tractId}/shared-acreage`, input);
    },
    onSuccess: (_data, variables) => {
      if (tractId == null) return;
      qc.invalidateQueries({ queryKey: leaseTractSharedAcreageKeys.detail(projectId, tractId) });
      qc.invalidateQueries({
        queryKey: leaseTractSharedAcreageKeys.detail(projectId, variables.target_tract_id),
      });
    },
  });
}

/** Unlinks `tractId` from `targetTractId`; same bidirectional invalidation
 * as {@link useAddSharedAcreage}. */
export function useRemoveSharedAcreage(tractId: string | null) {
  const qc = useQueryClient();
  const projectId = useProjectStore((s) => s.projectId);
  return useMutation({
    mutationFn: (targetTractId: string) => {
      if (tractId == null) {
        return Promise.reject(
          new Error('Cannot remove shared acreage before the tract is saved.'),
        );
      }
      return api.delete(`/lease-tracts/${tractId}/shared-acreage/${targetTractId}`);
    },
    onSuccess: (_data, targetTractId) => {
      if (tractId == null) return;
      qc.invalidateQueries({ queryKey: leaseTractSharedAcreageKeys.detail(projectId, tractId) });
      qc.invalidateQueries({
        queryKey: leaseTractSharedAcreageKeys.detail(projectId, targetTractId),
      });
    },
  });
}
